/**
 * ResetPassword — the emailed-link landing view (F14 FR8/FR10/FR14).
 *
 * The Console has NO router; App.jsx routes here when the browser lands on
 * `/reset-password?token=<raw>` (see hooks/useAuth.js). Unlike the L01
 * verify-email link this does NOT act on mount: the token alone must not change
 * anything, because a link preloader, a mail scanner or a chat-app unfurler
 * fetches URLs unasked and would otherwise burn the single-use token before the
 * customer clicked it. Nothing is sent until the user submits a new password.
 *
 * Confidentiality (FR8): the raw token arrives as a prop, is held in memory for
 * the one POST, and is never written to localStorage, never rendered, and never
 * interpolated into an error message.
 *
 * On success (FR10) the caller's `onResetComplete` clears the stored JWT and
 * scrubs the token-bearing URL from history, while this screen stays mounted to
 * show the confirmation. A reset deliberately does NOT sign the user in: the
 * backend returns 204 with no JWT, and they authenticate with the new password.
 *
 * Color semantics (.impeccable.md): ok(green) = the completed reset only,
 * warn(amber) = a dead link, which is recoverable and so is never red, and
 * danger(red) = a real submit failure. accent = interactive only.
 */

import { useState } from "react";
import * as api from "../engine/api";
import { checkPassword, PASSWORD_POLICY_HINT } from "../engine/passwordValidation";
import PasswordRequirements from "./PasswordRequirements";

/**
 * The two link states that no amount of retrying will fix, keyed off the stable
 * error `code` on the OMEGA-590 envelope rather than the human message (which is
 * copy and may change). Returning null means "not a dead link", so the caller
 * keeps the form and the typed passwords for correction.
 */
function deadLinkMessage(err) {
  if (err?.code === "token_already_used") {
    return "This reset link has already been used. If you still need to change your password, request a new link.";
  }
  if (err?.code === "invalid_or_expired_token") {
    return "This reset link is invalid or has expired. Request a new link to continue.";
  }
  return null;
}

export default function ResetPassword({ token, onResetComplete, onBackToLogin, onRequestNewLink }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // A link that can never work. Seeded from a missing token so a truncated or
  // hand-typed URL gets the same recoverable screen as an expired one, rather
  // than a form whose submit is guaranteed to fail.
  const [deadLink, setDeadLink] = useState(() =>
    token ? "" : "This reset link is missing its token. Request a new link to continue.",
  );

  const submit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (submitting) return;
    setError("");

    // Client-only confirm guard, same contract as PasswordPanel: a mismatch
    // never reaches the network and the confirmation is never sent.
    if (next !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    // Mirrors the backend policy so the checklist below can show which rule is
    // unmet; the backend still hard-rejects on its own.
    if (!checkPassword(next).valid) {
      setError("New password does not meet the requirements listed below.");
      return;
    }

    setSubmitting(true);
    try {
      await api.resetPassword(token, next);
      // Wipe both fields before anything renders: the new password has served
      // its purpose and there is no correction path left to preserve it for.
      setNext("");
      setConfirm("");
      setDone(true);
      // Clears the stored JWT and scrubs the token from the URL and history
      // (FR10). Called AFTER setDone so the success state is what the user sees.
      if (typeof onResetComplete === "function") onResetComplete();
    } catch (err) {
      const dead = deadLinkMessage(err);
      if (dead) {
        // FR14 — the fields are only worth preserving while they can still be
        // corrected into a success. Against a dead token they cannot, so drop
        // them rather than leave a password sitting in state with no use.
        //
        // These two lines are DELIBERATELY UNPINNED and must not be given a test
        // that pretends otherwise. Switching to the dead-link panel unmounts the
        // inputs and there is no route back to the form, so a cleared and an
        // uncleared state produce byte-identical DOM: any assertion written here
        // would be satisfied by the branch switch alone and would keep passing
        // after these lines were deleted (confirmed by mutation M18, which
        // survived a test that read as if it covered them). The counterpart on
        // the `else` branch IS observable, and is pinned. See the note in
        // ResetPassword.test.jsx.
        setNext("");
        setConfirm("");
        setDeadLink(dead);
      } else {
        // A policy 422, a 429, a 503 or a transport failure: all correctable
        // from the same form, so keep what the user typed.
        setError(err.message || "Could not reset your password. Try again shortly.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-ink rounded-none px-7 pt-7 pb-6">
          <div className="text-white font-semibold text-2xl tracking-[0.22em]">OMEGA AI HPC</div>
          <div className="text-ink-muted text-sm mt-1">Choose a new password</div>
        </div>
        <div className="bg-surface border border-line border-t-0 rounded-none px-7 py-6">{children}</div>
      </div>
    </div>
  );

  if (done) {
    return shell(
      <div>
        <div
          data-testid="reset-success"
          role="status"
          className="text-sm text-ok bg-ok-tint border border-ok/20 rounded-none px-3 py-2 mb-4 leading-relaxed"
        >
          Your password has been reset. For your security every signed-in session was ended, so
          sign in again with your new password.
        </div>
        <button
          type="button"
          onClick={onBackToLogin}
          data-testid="reset-continue"
          className="w-full py-2.5 rounded-none bg-accent-fill text-white text-sm font-medium cursor-pointer hover:bg-accent-strong transition-colors duration-150"
        >
          Continue to login
        </button>
      </div>,
    );
  }

  if (deadLink) {
    return shell(
      <div>
        <div
          data-testid="reset-dead-link"
          className="text-sm text-warn bg-warn-tint border border-warn/20 rounded-none px-3 py-2 mb-4 leading-relaxed"
        >
          {deadLink}
        </div>
        <button
          type="button"
          onClick={onRequestNewLink}
          data-testid="reset-request-new"
          className="w-full py-2.5 rounded-none bg-accent-fill text-white text-sm font-medium cursor-pointer hover:bg-accent-strong transition-colors duration-150"
        >
          Request a new link
        </button>
        <button
          type="button"
          onClick={onBackToLogin}
          data-testid="reset-back"
          className="w-full py-2 mt-2 text-sm text-accent cursor-pointer hover:text-accent-strong bg-transparent border-0"
        >
          Back to login
        </button>
      </div>,
    );
  }

  return shell(
    <form data-testid="reset-password-form" onSubmit={submit}>
      <label htmlFor="reset-new" className="block text-xs text-fg2 uppercase tracking-wide mb-1">
        New password
      </label>
      <input
        id="reset-new"
        type="password"
        value={next}
        onChange={(ev) => {
          setNext(ev.target.value);
          setError("");
        }}
        autoComplete="new-password"
        className="w-full px-3 py-2 rounded-none border border-line bg-surface text-fg text-sm outline-none focus:border-accent-fill"
      />
      <PasswordRequirements password={next} testId="reset-password-requirements" />

      <label htmlFor="reset-confirm" className="block text-xs text-fg2 uppercase tracking-wide mb-1 mt-4">
        Confirm new password
      </label>
      <input
        id="reset-confirm"
        type="password"
        value={confirm}
        onChange={(ev) => {
          setConfirm(ev.target.value);
          setError("");
        }}
        autoComplete="new-password"
        className="w-full px-3 py-2 mb-4 rounded-none border border-line bg-surface text-fg text-sm outline-none focus:border-accent-fill"
      />

      <div className="text-xs text-fg3 mb-4">New password must be {PASSWORD_POLICY_HINT}</div>

      {error && (
        <div
          data-testid="reset-error"
          className="text-sm text-danger bg-danger-tint border border-danger/20 rounded-none px-3 py-2 mb-4"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        data-testid="reset-submit"
        disabled={submitting || !next || !confirm}
        aria-busy={submitting}
        className="w-full py-2.5 rounded-none bg-accent-fill text-white text-sm font-medium cursor-pointer hover:bg-accent-strong transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? "Saving..." : "Set new password"}
      </button>

      <button
        type="button"
        onClick={onBackToLogin}
        data-testid="reset-back"
        className="w-full py-2 mt-2 text-sm text-accent cursor-pointer hover:text-accent-strong bg-transparent border-0"
      >
        Back to login
      </button>
    </form>,
  );
}
